import { useMemo, useState } from 'react';
import { DemeterPageElementModel } from '../../../../Generated/Raven-Demeter';
import useLanguage from '../../../Services/Language/useLanguageHook';
import trackingService, { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import AutoCompleteInput, { AutoCompleteOption } from '../../Form/Inputs/AutoCompleteInput';
import { IPageElementModalSelection } from './PageElementModalDefinitions';

interface IPageElementSearchProps {
    pageElements: DemeterPageElementModel[];
    handleSelect: (selected: IPageElementModalSelection) => void;
    showExchangeAndCommodity: boolean;
    showRegionAndElement: boolean;
    showContractNumbers: boolean;
    showDataFrequency: boolean;
    track: TrackingEvent;
}

const PageElementSearch: React.FC<IPageElementSearchProps> = (props) => {
    const [translations, translate] = useLanguage();
    const [selectedLabel, setSelectedLabel] = useState<string>('');

    const uniqueSelections: IPageElementModalSelection[] = useMemo(() => {
        const byKey = new Map<string, IPageElementModalSelection>();

        props.pageElements.forEach((pageElement) => {
            const selection: IPageElementModalSelection = {
                market: pageElement.market,
                pageType: pageElement.pageType,
                dataSource: pageElement.dataSource,
                exchange: pageElement.exchange,
                commodity: pageElement.commodity,
                subCommodity: pageElement.subCommodity,
                region: pageElement.region,
                subRegion: pageElement.subRegion,
                commodityName: pageElement.commodityDisplayName || pageElement.commodity,
                subCommodityName: pageElement.subCommodityDisplayName || pageElement.subCommodity,
                subRegionName: pageElement.subRegionDisplayName || pageElement.subRegion,
                extraParameters: pageElement.extraParameters,
                dataFrequency: pageElement.dataFrequency,
            };

            const key = [
                selection.market ?? '',
                selection.pageType ?? '',
                selection.dataSource ?? '',
                selection.exchange ?? '',
                selection.commodity ?? '',
                selection.subCommodity ?? '',
                selection.region ?? '',
                selection.subRegion ?? '',
                selection.dataFrequency ?? '',
                selection.extraParameters ?? '',
            ].join('|');

            if (!byKey.has(key)) {
                byKey.set(key, selection);
            }
        });

        return Array.from(byKey.values());
    }, [props.pageElements]);

    const { mappedOptions, selectionByLabel } = useMemo(() => {
        const buildLabel = (selection: IPageElementModalSelection): string => {
            const pageTypeName =
                selection.pageType && translations.pageType?.[selection.pageType] ? translations.pageType[selection.pageType] : selection.pageType;

            const exchangeName =
                selection.exchange && translations.exchange?.[selection.exchange] ? translations.exchange[selection.exchange] : selection.exchange;

            const regionName = selection.region && translations.region?.[selection.region] ? translations.region[selection.region] : selection.region;

            const commodityLabel = selection.commodityName ? translate(selection.commodityName) ?? selection.commodityName : selection.commodity;

            const subCommodityLabel = selection.subCommodityName ? translate(selection.subCommodityName) || selection.subCommodityName : selection.subCommodity;

            const subRegionLabel = selection.subRegionName ? translate(selection.subRegionName) || selection.subRegionName : selection.subRegion;

            const pageElementName =
                selection.pageElementType && translations.pageElementType?.[selection.pageElementType]
                    ? translations.pageElementType[selection.pageElementType]
                    : selection.pageElementType;

            const breadcrumbParts: string[] = [];

            if (pageTypeName) {
                breadcrumbParts.push(pageTypeName);
            }

            if (props.showExchangeAndCommodity) {
                if (selection.dataSource) {
                    breadcrumbParts.push(selection.dataSource);
                }
                if (exchangeName) {
                    breadcrumbParts.push(exchangeName);
                }
                if (commodityLabel) {
                    breadcrumbParts.push(commodityLabel);
                }
                if (subCommodityLabel) {
                    breadcrumbParts.push(subCommodityLabel);
                }
            }

            if (props.showRegionAndElement) {
                if (regionName) {
                    breadcrumbParts.push(regionName);
                }
                if (subRegionLabel) {
                    breadcrumbParts.push(subRegionLabel);
                }
            }

            if (props.showDataFrequency) {
                if (selection.dataFrequency) {
                    const dataFrequencyName =
                        selection.dataFrequency && translations.dataFrequency?.[selection.dataFrequency]
                            ? translations.dataFrequency[selection.dataFrequency]
                            : selection.dataFrequency;
                    breadcrumbParts.push(dataFrequencyName);
                }
            }

            if (pageElementName) {
                breadcrumbParts.push(pageElementName);
            }

            return breadcrumbParts.join(' / ');
        };

        const items = uniqueSelections.map((selection) => ({
            selection,
            baseLabel: buildLabel(selection),
        }));

        const baseCounts = items.reduce((acc, item) => {
            acc.set(item.baseLabel, (acc.get(item.baseLabel) ?? 0) + 1);
            return acc;
        }, new Map<string, number>());

        const runningIndex = new Map<string, number>();
        const selectionMap = new Map<string, IPageElementModalSelection>();

        const optionsList: AutoCompleteOption[] = items.map((item) => {
            let label = item.baseLabel;

            if ((baseCounts.get(item.baseLabel) ?? 0) > 1) {
                const next = (runningIndex.get(item.baseLabel) ?? 0) + 1;
                runningIndex.set(item.baseLabel, next);
                label = `${item.baseLabel} (${next})`;
            }

            selectionMap.set(label, item.selection);

            return {
                label,
            } as AutoCompleteOption;
        });

        return {
            mappedOptions: optionsList,
            selectionByLabel: selectionMap,
        };
    }, [uniqueSelections, props.showExchangeAndCommodity, props.showRegionAndElement, props.showDataFrequency, translate, translations]);

    const handleTextChange = (labelValue: string): void => {
        setSelectedLabel(labelValue);

        if (!labelValue) {
            props.handleSelect({});
            return;
        }

        const selectedSelection = selectionByLabel.get(labelValue);
        if (!selectedSelection) {
            return;
        }

        props.handleSelect(selectedSelection);

        trackingService.track({ ...props.track, eventValue: labelValue });
    };

    return <AutoCompleteInput title="" labelValue={selectedLabel} options={mappedOptions} handleTextChange={handleTextChange} track={props.track} />;
};

export default PageElementSearch;
